import {
  type TerraUIMessage,
  type ToolCallGenericMessage,
  type BaseDataMessage,
  type TodosMessage,
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  StreamConfirmationPartTypeName,
} from '@terra/shared-dto-chat-types';
import { isPersistentSubAgentTool } from '../components/chat-headline-actions';
import { isToolCallData } from './tool-call-data-merge';
import {
  isDataOrConfirmationPart,
  parseBaseDataMessage,
} from './hitl-confirmation-matching';
import type {
  NeoAttackTrackingCache,
  NeoAttackTrackingPartRef,
  NeoAttackTrackingStateCache,
} from './message-processing-types';

export function isAttackerNeoAwaitingOutput(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }

  const record = output as {
    awaitingCompletion?: boolean;
    neoThreadId?: unknown;
  };

  return (
    record.awaitingCompletion === true && typeof record.neoThreadId === 'string'
  );
}

function readAttackerNeoThreadId(output: unknown): string | undefined {
  if (typeof output !== 'object' || output === null) {
    return undefined;
  }

  const neoThreadId = (output as { neoThreadId?: unknown }).neoThreadId;

  return typeof neoThreadId === 'string' ? neoThreadId : undefined;
}

function isTerminalAttackerToolCall(data: ToolCallGenericMessage): boolean {
  if (
    data.status === 'completed' ||
    data.status === 'error' ||
    data.status === 'aborted'
  ) {
    return true;
  }

  return (
    data.type === DataMessageTypeEnum.ToolCallComplete ||
    data.type === DataMessageTypeEnum.ToolCallError
  );
}

function collectCompletedAttackerNeoSignals(messages: TerraUIMessage[]): {
  completedToolCallIds: Set<string>;
  completedNeoThreadIds: Set<string>;
} {
  const completedToolCallIds = new Set<string>();
  const completedNeoThreadIds = new Set<string>();

  for (const message of messages) {
    if (message.role !== 'assistant') {
      continue;
    }

    for (const part of message.parts) {
      if (!isDataOrConfirmationPart(part) || !part.data) {
        continue;
      }

      const data = parseBaseDataMessage(part.data);

      if (!data || !isToolCallData(data) || data.toolName !== 'attacker-tool') {
        continue;
      }

      const output = data.output;
      const hasSuccess =
        typeof output === 'object' &&
        output !== null &&
        (output as { success?: unknown }).success !== undefined;
      const neoThreadId = readAttackerNeoThreadId(output);
      const isCompleted = isTerminalAttackerToolCall(data) || hasSuccess;

      if (!isCompleted) {
        continue;
      }

      if (typeof data.toolCallId === 'string') {
        completedToolCallIds.add(data.toolCallId);
      }

      if (neoThreadId) {
        completedNeoThreadIds.add(neoThreadId);
      }
    }
  }

  return { completedToolCallIds, completedNeoThreadIds };
}

export function chatHasActiveNeoAttackTracking(
  messages: TerraUIMessage[],
): boolean {
  const { completedToolCallIds, completedNeoThreadIds } =
    collectCompletedAttackerNeoSignals(messages);

  for (const message of messages) {
    if (message.role !== 'assistant') {
      continue;
    }

    for (const part of message.parts) {
      if (!isDataOrConfirmationPart(part) || !part.data) {
        continue;
      }

      const data = parseBaseDataMessage(part.data);

      if (!data || !isToolCallData(data) || data.toolName !== 'attacker-tool') {
        continue;
      }

      if (data.status !== 'in-progress') {
        continue;
      }

      const output = data.output;

      if (typeof output !== 'object' || output === null) {
        continue;
      }

      const record = output as {
        neoThreadId?: unknown;
        success?: unknown;
        awaitingCompletion?: unknown;
      };

      if (typeof record.neoThreadId !== 'string') {
        continue;
      }

      if (record.awaitingCompletion !== true) {
        continue;
      }

      if (record.success !== undefined) {
        continue;
      }

      if (
        typeof data.toolCallId === 'string' &&
        completedToolCallIds.has(data.toolCallId)
      ) {
        continue;
      }

      if (completedNeoThreadIds.has(record.neoThreadId)) {
        continue;
      }

      return true;
    }
  }

  return false;
}

function appendNeoAttackTrackingIdentityEntry({
  identity,
  messageIndex,
  partIndex,
  data,
}: {
  identity: string;
  messageIndex: number;
  partIndex: number;
  data: ToolCallGenericMessage;
}): string {
  const output = data.output;
  let neoSnapshot = '';

  if (typeof output === 'object' && output !== null) {
    const record = output as {
      neoThreadId?: unknown;
      awaitingCompletion?: unknown;
      success?: unknown;
    };

    neoSnapshot =
      `${String(record.neoThreadId ?? '')}:` +
      `${String(record.awaitingCompletion ?? '')}:` +
      `${record.success !== undefined ? '1' : '0'}`;
  }

  const entry =
    `${messageIndex}:${partIndex}:${data.type}:${data.status}:` +
    `${data.toolCallId ?? ''}:${neoSnapshot}`;

  return identity.length > 0 ? `${identity}|${entry}` : entry;
}

function messageHasAttackerToolPart(
  message: TerraUIMessage | undefined,
): boolean {
  if (!message || message.role !== 'assistant') {
    return false;
  }

  for (const part of message.parts) {
    if (part.type !== TerraUIMessageDataPartName || !part.data) {
      continue;
    }

    const data = parseBaseDataMessage(part.data);

    if (data && isToolCallData(data) && data.toolName === 'attacker-tool') {
      return true;
    }
  }

  return false;
}

function computeAssistantDataPartCountSignature(
  messages: TerraUIMessage[],
): string {
  let signature = '';

  for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
    const message = messages[messageIndex];

    if (!message || message.role !== 'assistant') {
      continue;
    }

    let dataPartCount = 0;

    for (const part of message.parts) {
      if (
        part.type === TerraUIMessageDataPartName ||
        part.type === StreamConfirmationPartTypeName
      ) {
        dataPartCount++;
      }
    }

    if (signature.length > 0) {
      signature += '|';
    }

    signature += `${messageIndex}:${dataPartCount}`;
  }

  return signature;
}

function computeNeoAttackTrackingIdentityFromRefs({
  messages,
  refs,
}: {
  messages: TerraUIMessage[];
  refs: NeoAttackTrackingPartRef[];
}): string | null {
  let identity = '';

  for (const { messageIndex, partIndex } of refs) {
    const message = messages[messageIndex];

    if (!message || message.role !== 'assistant') {
      return null;
    }

    if (partIndex >= message.parts.length) {
      return null;
    }

    const part = message.parts[partIndex];

    if (!part || !isDataOrConfirmationPart(part) || !part.data) {
      return null;
    }

    const data = parseBaseDataMessage(part.data);

    if (!data || !isToolCallData(data) || data.toolName !== 'attacker-tool') {
      return null;
    }

    identity = appendNeoAttackTrackingIdentityEntry({
      identity,
      messageIndex,
      partIndex,
      data,
    });
  }

  return identity;
}

function scanNeoAttackTrackingAttackerParts(messages: TerraUIMessage[]): {
  identity: string;
  refs: NeoAttackTrackingPartRef[];
} {
  let identity = '';
  const refs: NeoAttackTrackingPartRef[] = [];

  for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
    const message = messages[messageIndex];

    if (!message || message.role !== 'assistant') {
      continue;
    }

    for (let partIndex = 0; partIndex < message.parts.length; partIndex++) {
      const part = message.parts[partIndex];

      if (part.type !== TerraUIMessageDataPartName || !part.data) {
        continue;
      }

      const data = parseBaseDataMessage(part.data);

      if (!data || !isToolCallData(data) || data.toolName !== 'attacker-tool') {
        continue;
      }

      refs.push({ messageIndex, partIndex });
      identity = appendNeoAttackTrackingIdentityEntry({
        identity,
        messageIndex,
        partIndex,
        data,
      });
    }
  }

  return { identity, refs };
}

function tryResolveNeoAttackTrackingIdentity({
  messages,
  cache,
  assistantDataPartCountSignature,
}: {
  messages: TerraUIMessage[];
  cache: NeoAttackTrackingStateCache | null;
  assistantDataPartCountSignature: string;
}): { identity: string; refs: NeoAttackTrackingPartRef[] } | null {
  if (!cache) {
    return null;
  }

  if (
    assistantDataPartCountSignature !== cache.assistantDataPartCountSignature
  ) {
    return null;
  }

  const identityFromRefs = computeNeoAttackTrackingIdentityFromRefs({
    messages,
    refs: cache.refs,
  });

  if (identityFromRefs === null) {
    return null;
  }

  if (messages.length > cache.scannedMessageCount) {
    for (
      let messageIndex = cache.scannedMessageCount;
      messageIndex < messages.length;
      messageIndex++
    ) {
      if (messageHasAttackerToolPart(messages[messageIndex])) {
        return null;
      }
    }

    return { identity: identityFromRefs, refs: cache.refs };
  }

  if (messages.length < cache.scannedMessageCount) {
    return null;
  }

  return { identity: identityFromRefs, refs: cache.refs };
}

export function computeNeoAttackTrackingIdentity(
  messages: TerraUIMessage[],
): string {
  return scanNeoAttackTrackingAttackerParts(messages).identity;
}

export function resolveChatHasActiveNeoAttackTracking({
  messages,
  cache,
  identity: identityOverride,
}: {
  messages: TerraUIMessage[];
  cache: NeoAttackTrackingCache | null;
  identity?: string;
}): { result: boolean; cache: NeoAttackTrackingCache } {
  const identity =
    identityOverride ?? computeNeoAttackTrackingIdentity(messages);

  if (cache?.identity === identity) {
    return { result: cache.result, cache };
  }

  const result = chatHasActiveNeoAttackTracking(messages);

  return { result, cache: { identity, result } };
}

export function resolveNeoAttackTrackingState({
  messages,
  cache,
}: {
  messages: TerraUIMessage[];
  cache: NeoAttackTrackingStateCache | null;
}): {
  hasActiveNeoAttackTracking: boolean;
  neoAttackTrackingIdentity: string;
  cache: NeoAttackTrackingStateCache;
} {
  const assistantDataPartCountSignature =
    computeAssistantDataPartCountSignature(messages);
  const fastPath = tryResolveNeoAttackTrackingIdentity({
    messages,
    cache,
    assistantDataPartCountSignature,
  });
  const { identity, refs } =
    fastPath ?? scanNeoAttackTrackingAttackerParts(messages);

  if (
    cache?.identity === identity &&
    cache.assistantDataPartCountSignature === assistantDataPartCountSignature
  ) {
    return {
      hasActiveNeoAttackTracking: cache.result,
      neoAttackTrackingIdentity: identity,
      cache: {
        refs,
        identity,
        result: cache.result,
        scannedMessageCount: messages.length,
        assistantDataPartCountSignature,
      },
    };
  }

  const resolved = resolveChatHasActiveNeoAttackTracking({
    messages,
    cache: cache ? { identity: cache.identity, result: cache.result } : null,
    identity,
  });

  return {
    hasActiveNeoAttackTracking: resolved.result,
    neoAttackTrackingIdentity: identity,
    cache: {
      refs,
      identity,
      result: resolved.result,
      scannedMessageCount: messages.length,
      assistantDataPartCountSignature,
    },
  };
}

function finalizeStaleToolCallStatus<T extends BaseDataMessage>(
  dataMessage: T,
  {
    hasActiveNeoAttackTracking,
    pendingHitlToolCallIds,
  }: {
    hasActiveNeoAttackTracking: boolean;
    pendingHitlToolCallIds: Set<string>;
  },
): T {
  if (!isToolCallData(dataMessage)) return dataMessage;
  if (
    dataMessage.status !== 'started' &&
    dataMessage.status !== 'in-progress'
  ) {
    return dataMessage;
  }

  const toolCallId = dataMessage.toolCallId ?? dataMessage.id;

  if (toolCallId && pendingHitlToolCallIds.has(toolCallId)) {
    return dataMessage;
  }

  if (isPersistentSubAgentTool(dataMessage.toolName)) {
    return dataMessage;
  }

  if (dataMessage.metadata?.backgroundChild === true) {
    return dataMessage;
  }

  if (
    hasActiveNeoAttackTracking &&
    dataMessage.toolName === 'attacker-tool' &&
    isAttackerNeoAwaitingOutput(dataMessage.output)
  ) {
    return dataMessage;
  }

  if (
    dataMessage.toolName === 'attacker-tool' &&
    isAttackerNeoAwaitingOutput(dataMessage.output)
  ) {
    const neoThreadId = (dataMessage.output as { neoThreadId: string })
      .neoThreadId;

    return {
      ...dataMessage,
      status: 'in-progress',
      type: DataMessageTypeEnum.ToolCallUpdate,
      output: {
        neoThreadId,
        awaitingCompletion: false,
      },
    } as T;
  }

  return {
    ...dataMessage,
    status: 'completed',
    type: DataMessageTypeEnum.ToolCallComplete,
  } as T;
}

function finalizeStaleTodosStatus(
  dataMessage: TodosMessage,
  { hasActiveNeoAttackTracking }: { hasActiveNeoAttackTracking: boolean },
): TodosMessage {
  const todos = dataMessage.todos;

  if (!todos?.some((todo) => todo.state === 'in_progress')) {
    return dataMessage;
  }

  if (hasActiveNeoAttackTracking) {
    return dataMessage;
  }

  return {
    ...dataMessage,
    todos: todos.map((todo) =>
      todo.state === 'in_progress'
        ? { ...todo, state: 'cancelled' as const }
        : todo,
    ),
  };
}

export function finalizeStaleStreamDataMessage<T extends BaseDataMessage>(
  dataMessage: T,
  {
    hasActiveNeoAttackTracking,
    pendingHitlToolCallIds,
  }: {
    hasActiveNeoAttackTracking: boolean;
    pendingHitlToolCallIds: Set<string>;
  },
): T {
  const afterToolCall = finalizeStaleToolCallStatus(dataMessage, {
    hasActiveNeoAttackTracking,
    pendingHitlToolCallIds,
  });

  if (afterToolCall.type !== DataMessageTypeEnum.Todos) {
    return afterToolCall;
  }

  return finalizeStaleTodosStatus(afterToolCall as TodosMessage, {
    hasActiveNeoAttackTracking,
  }) as T;
}
